import { Guid } from 'guid-typescript';
import { notEmpty } from '@/helpers';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import duration from 'dayjs/plugin/duration';

dayjs.extend(advancedFormat);
dayjs.extend(relativeTime);
dayjs.extend(duration);

declare global {
    interface Array<T> {
        /** Removes any null or undefined values from the array. */
        notEmpty(): Array<T extends null | undefined ? never : T>;
    }
    interface Date {
        /** Creates a relative date from now or the specified date. */
        getRelative(fromDate?: Date): string;
    }
    interface String {
        capitalizeFirstLetter(): string;
    }
}

/**
 * Override the toJSON function on the Guid class from package 'guid-typescript'.
 * The toJSON function is used when serializing data, e.g. when posting data to the api.
 * We need it to serialize as the string value only, not as an object like it does by default.
 */
Guid.prototype.toJSON = function (): string {
    return this.value;
};

/**
 * Check if something is equal to the Guid. If the something is a string, it will be parsed as a Guid.
 */
Guid.prototype.isEqualTo = function (other: unknown): boolean {
    if (other == null) return false;

    if (typeof other === 'string') {
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        return this.equals(Guid.parse(other));
    }

    if (!(other instanceof Guid)) return false;
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    return this.equals(other);
};

Array.prototype.notEmpty = function <T>(this: (T | undefined | null)[]): T[] {
    return this.filter(notEmpty);
};

/**
 * Creates a relative date from now or the specified date.
 * @param fromDate The origin date to be relative to. If not set, it will be from the current date and time.
 */
Date.prototype.getRelative = function (this: Date, fromDate?: Date): string {
    const dateOrigin = fromDate == null
        ? new Date()
        : new Date(fromDate);

    const relativeValue = dayjs().from(dateOrigin);
    return relativeValue;
};

String.prototype.capitalizeFirstLetter = function (this: string): string {
    if (this.length === 0) {
        return this;
    }
    return this.charAt(0).toLocaleUpperCase() + this.slice(1);
};
